import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import useDialog from "../../../hooks/dialog/useDialog";
import { useAutocomplete } from "../../../hooks/forms/FormHooks";
import { resendBillingPdf, sendBillingPdf } from "../../../services/api/api";
import { formatDate } from "../../../services/date";
import CustomAutoComplete from "../../shared/autoCompletes/CustomAutoComplete";
import CustomLoadingButton from "../../shared/button/CustomLoadingButton";
import CustomContentDialog from "../../shared/dialog/CustomContentDialog";

const defaultBillingKinds = [{ name: "ENTRADA" }, { name: "VISTORIA" }, { name: "INTEGRAL" }];

const paymentKinds = {
  1: "Entrada",
  2: "Vistoria",
  3: "Integral",
};

const paymentStatus = {
  PAID: "Recebido",
  MARKED_AS_PAID: "Marcado c/ recebido",
  WAITING: "A receber",
  OVERDUE: "Atrasado",
  EXPIRED: "Expirado",
  CANCELLED: "Cancelado",
};

const paymentColors = {
  PAID: "#00a04e",
  MARKED_AS_PAID: "#71a846",
  WAITING: "#0094dd",
  OVERDUE: "#ffbc31",
  EXPIRED: "#abacb0",
  CANCELLED: "#ff474c",
};

const PaymentsHistory = ({
  projectId,
  isUniqueCharge,
  clearAllFields,
  isDisabled,
  setDisabled,
  initializeLoadingToast,
  updateLoadingToast,
  paymentsHistoryData = [],
  paymentsHistoryLoading = false,
}) => {
  const theme = useTheme();
  const [billingKinds, setBillingKinds] = useState(defaultBillingKinds);
  const [isSendBillLoading, setIsSendBillLoading] = useState(false);
  const [isResendBillLoading, setIsResendBillLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const { reset: resetBillingKind, ...billingKind } = useAutocomplete();
  const queryClient = useQueryClient();

  useEffect(() => {
    let tempBillingKinds = defaultBillingKinds;

    // This Line of code assures that if the client is selected for full charges, only
    // the full charge option appears to him
    if (isUniqueCharge) {
      tempBillingKinds = tempBillingKinds.filter((item) => item.name === "INTEGRAL");
    }

    const isPaymentStatusValid = (status) =>
      status && status !== "EXPIRED" && status !== "CANCELLED";

    if (!paymentsHistoryLoading && paymentsHistoryData.length !== 0) {
      // This Line of code assures that, even if the client is selected for full charges,
      // if a two-way charge is already sent for him, the another one can be sent too.
      tempBillingKinds = defaultBillingKinds;

      // Iterate over the payments to determine the appropriate billing kinds
      paymentsHistoryData.forEach((p) => {
        if (p.paymentKindId === 3 && isPaymentStatusValid(p.status)) {
          tempBillingKinds = [];
          return; // Exit the loop early if the condition is met
        } else if (p.paymentKindId === 2 && isPaymentStatusValid(p.status)) {
          tempBillingKinds = tempBillingKinds.filter((item) => item.name === "ENTRADA");
        } else if (p.paymentKindId === 1 && isPaymentStatusValid(p.status)) {
          tempBillingKinds = tempBillingKinds.filter((item) => item.name === "VISTORIA");
        }
      });
    }
    setBillingKinds(tempBillingKinds);
  }, [paymentsHistoryData, isUniqueCharge]);

  const headCells = [
    { id: "paymentKindId", label: "Cobrança" },
    { id: "chargeDate", label: "Emissão" },
    { id: "chargeValue", label: "Valor" },
    { id: "status", label: "Status" },
    { id: "actions", label: "Ações" },
  ];

  const {
    open: billDialogOpen,
    handleClickOpen: handleBillDialogOpen,
    handleClickClose: handleBillDialogClose,
  } = useDialog();

  const {
    open: resendBillDialogOpen,
    handleClickOpen: handleResendBillDialogOpen,
    handleClickClose: handleResendBillDialogClose,
  } = useDialog();

  const dialogContent = (
    <CustomAutoComplete
      params={billingKind}
      options={billingKinds}
      disabled={isDisabled}
      label="Tipo de Pagamento"
      width="100%"
    />
  );

  const handleSendBillClick = async () => {
    let sendBillToastId;
    setIsSendBillLoading(true);
    setDisabled(true);
    try {
      handleBillDialogClose();
      sendBillToastId = initializeLoadingToast({
        text: `Enviando Cobrança...`,
      });

      const { status } = await sendBillingPdf(projectId, billingKind.value.name);

      if (status === 200) {
        clearAllFields();

        updateLoadingToast({
          id: sendBillToastId,
          text: `Cobrança em processo de envio ao integrador`,
          type: "success",
        });
        queryClient.invalidateQueries({ queryKey: ["paymentsHistory"] });
      } else {
        throw new Error();
      }
    } catch (error) {
      updateLoadingToast({
        id: sendBillToastId,
        text: error.response.data.message ?? `Erro ao enviar cobrança, tente novamente`,
        type: "error",
      });
    } finally {
      setIsSendBillLoading(false);
      setDisabled(false);
    }
  };

  const handleResendBillClick = async () => {
    const { projectId, paymentKind } = selectedPayment;

    let sendBillToastId;
    setIsResendBillLoading(true);
    setDisabled(true);
    try {
      sendBillToastId = initializeLoadingToast({
        text: `Reenviando PDF de Cobrança...`,
      });

      await resendBillingPdf(projectId, paymentKind);

      updateLoadingToast({
        id: sendBillToastId,
        text: `PDF de Cobrança em processo de reenvio ao integrador`,
        type: "success",
      });

      setSelectedPayment(null);
      handleResendBillDialogClose();
    } catch (error) {
      updateLoadingToast({
        id: sendBillToastId,
        text: error.response.data.message ?? `Erro ao reenviar PDF de cobrança, tente novamente`,
        type: "error",
      });
    } finally {
      setIsResendBillLoading(false);
      setDisabled(false);
    }
  };

  const isResendButtonDisaled = (p) => {
    return (
      p.bankPaymentId === null ||
      p.bankPaymentId === undefined ||
      p.status === "PAID" ||
      p.status === "MARKED_AS_PAID" ||
      p.status === "EXPIRED" ||
      p.status === "CANCELLED"
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Botão Boleto */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <CustomLoadingButton
          color={theme.palette.info.main}
          hoverColor={theme.palette.info.hover}
          startIcon={<WhatsAppIcon />}
          buttonText="Boleto"
          buttonLoadingText="Gerando..."
          sx={{ height: 36 }}
          disabled={isDisabled}
          handleButtonClick={handleBillDialogOpen}
          requestLoading={isSendBillLoading}
        />
      </Box>

      <CustomContentDialog
        open={billDialogOpen}
        handleClickClose={handleBillDialogClose}
        title="Enviar Boleto/Pix"
        content={dialogContent}
        requestLoading={isSendBillLoading}
        handleConfirmationClick={handleSendBillClick}
        confirmationClickText="Enviar"
        requestLoadingText="Enviando..."
        width={500}
        height={100}
      />

      <CustomContentDialog
        open={resendBillDialogOpen}
        handleClickClose={handleResendBillDialogClose}
        title="Reenviar PDF de Boleto/Pix"
        content="Tem certeza que deseja reenviar o PDF de boleto/pix para o integrador?"
        requestLoading={isResendBillLoading}
        handleConfirmationClick={handleResendBillClick}
        requestLoadingText="Reenviando..."
        confirmationClickText="Reenviar"
        width={600}
        height={50}
      />

      {!paymentsHistoryLoading ? (
        paymentsHistoryData.length !== 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {headCells.map((headCell, i) => (
                    <TableCell key={i}>{headCell.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paymentsHistoryData.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>{paymentKinds[item.paymentKindId]}</TableCell>
                    <TableCell>{item.chargeDate ? formatDate(item.chargeDate) : ""}</TableCell>
                    <TableCell>{`R$ ${item.chargeValue}`}</TableCell>
                    <TableCell sx={{ maxWidth: 100, textAlign: "center" }}>
                      <Box
                        sx={{
                          backgroundColor: paymentColors[item.status],
                          p: 0.4,
                          borderRadius: 1,
                          textAlign: "center",
                        }}
                      >
                        {paymentStatus[item.status]}
                      </Box>
                      {(item.status === "PAID" || item.status === "MARKED_AS_PAID") && (
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                          {item.paymentDate ? formatDate(item.paymentDate) : ""}
                        </Typography>
                      )}
                      {item.status === "EXPIRE" && (
                        <Typography variant="caption">{formatDate(item.expireDate)}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 100, textAlign: "center" }}>
                      <Tooltip
                        title="Reenviar PDF a cliente"
                        placement="bottom"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: "0.7rem",
                            },
                          },
                        }}
                      >
                        <IconButton
                          disabled={isResendButtonDisaled(item) || isDisabled}
                          onClick={() => {
                            setSelectedPayment({
                              projectId: item.projectId,
                              paymentKind: item.paymentKinds.name,
                            });
                            handleResendBillDialogOpen();
                          }}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            sx={{
              width: "100%",
              textAlign: "center",
              fontWeight: "medium",
              fontSize: "1.1rem",
              opacity: 0.7,
              mt: 2,
            }}
          >
            Nenhuma cobrança cadastrada
          </Typography>
        )
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
};

export default PaymentsHistory;
